import { FieldModel, ModelingMetric } from '@cibo/core'
import {
  ReadOnlyPaginatedFieldsTable,
  SelectablePaginatedFieldsTable,
  fieldAcresColumn,
  fieldNameLocationColumn,
  fieldThumbnailColumn,
} from '@cibo/landmanager'
import { Loading, useUser } from '@cibo/profile'
import { useAllProgramContent } from '@cibo/programs'
import { Markdown } from '@cibo/ui'
import HandshakeRounded from '@mui/icons-material/HandshakeRounded'
import ThumbDownAltRounded from '@mui/icons-material/ThumbDownAltRounded'
import { Checkbox, Stack, Typography } from '@mui/material'
import { GridRenderCellParams, GridValueGetterParams } from '@mui/x-data-grid-pro'
import { flatten, uniqBy } from 'ramda'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useAgreement } from '../queries'
import { AgreementDeclinedItems } from '../types'

export const OfferAgreementFieldsTable = ({ agreementId }: { agreementId: string }) => {
  const agreement = useAgreement(agreementId)
  const { t } = useTranslation('@impact/pages/Agreements/AgreementFieldsTable')
  const user = useUser()

  const programContent = useAllProgramContent()

  const defaultColumns = useMemo(
    () =>
      [
        fieldThumbnailColumn({ t }),
        fieldNameLocationColumn({ t }),
        fieldAcresColumn({ t }),
        {
          headerName: t('program'),
          field: 'program',
          minWidth: 200,
          valueGetter: (p: GridValueGetterParams<any, FieldModel>) => {
            const programArray = uniqBy(
              a => a.programId,
              (p.row.participatingPrograms && flatten(p.row.participatingPrograms)) || []
            )

            return programArray
              .map(
                prog =>
                  programContent.data?.find(({ programId }) => prog.programId === programId)
                    ?.content.fields.title
              )
              .join(', ')
          },
        },
        {
          headerName: t('paymentAmount', { context: agreement.data?.status }),
          field: 'paymentAmount',
          valueGetter: (p: GridValueGetterParams<any, FieldModel>) =>
            `$${p.row.offerPayment() || '--'}`,
          minWidth: 200,
        },
        {
          headerName: t('carbonRemoval'),
          field: 'carbonRemoval',
          valueGetter: (p: GridValueGetterParams<any, FieldModel>) =>
            p.row.offer({ offerGroup: agreement.data?.offerGroup })?.input.modelingMetrics,
          renderCell: ({ value }: GridRenderCellParams<ModelingMetric[]>) => (
            <Markdown sx={{ mb: 0 }}>
              {value !== undefined
                ? // The goal is that we would be able to reach a state where we display the below
                  // However at present TT cannot send an accurate unitCode. I don't know why.
                  // ? value.map(a => `${a.measure} ${a.unitCode}`).join('  \n')
                  value.map(a => `${a.measure} MT CO₂e`).join('  \n')
                : '--'}
            </Markdown>
          ),
          minWidth: 200,
        },
      ].filter(column => !!column),
    [programContent.isFetched, agreement.data?.offerGroup]
  )

  if (agreement.isLoading || user.isLoading) {
    return <Loading />
  }

  const ownsAgreement = agreement.data && user.data?.matchUserId(agreement.data.userId)

  const baseQuery = { agreementId, includeAllResolved: true }

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1} alignItems="center">
        {(agreement.data?.status === 'agreed' || agreement.data?.status === 'concluded') && (
          <>
            <HandshakeRounded color="success" />
            <Typography variant="h5">{t('acceptedFields')}</Typography>
          </>
        )}
      </Stack>
      {ownsAgreement && agreement.data?.status === 'pending' ? (
        <SelectablePaginatedFieldsTable
          baseQuery={baseQuery}
          columns={defaultColumns}
          components={{
            BaseCheckbox: props => <Checkbox color="success" {...props} />,
          }}
        />
      ) : (
        <ReadOnlyPaginatedFieldsTable baseQuery={baseQuery} columns={defaultColumns} />
      )}
    </Stack>
  )
}
export const OfferAgreementDeclinedFieldsTable = ({
  declined,
}: {
  declined?: AgreementDeclinedItems[]
}) => {
  const { t } = useTranslation('@impact/pages/Agreements/AgreementFieldsTable')
  const defaultColumns = useMemo(
    () =>
      [
        fieldThumbnailColumn({ t }),
        fieldNameLocationColumn({ t }),
        fieldAcresColumn({ t }),
        {
          headerName: t('paymentAmount', { context: 'declined' }),
          field: 'paymentAmount',
          valueGetter: (p: GridValueGetterParams<any, FieldModel>) =>
            `$${p.row.offerPayment() || '--'}`,
          minWidth: 120,
        },
        {
          headerName: t('reason'),
          field: 'declinedReason',
          valueGetter: (p: GridValueGetterParams<any, FieldModel>) => {
            return p.row.resolveStandingDetail('offer').input?.declinedReason
          },
          minWidth: 250,
        },
      ].filter(column => !!column),
    []
  )
  const resourceIds = useMemo(() => declined?.map(d => d.id).join(','), [declined])

  const baseQuery = { resourceIds, includeAllResolved: true }

  if (!declined || declined.length === 0) {
    return null
  }

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1} alignItems="center">
        <ThumbDownAltRounded color="error" />
        <Typography variant="h5">{t('declinedFields')}</Typography>
      </Stack>

      <ReadOnlyPaginatedFieldsTable baseQuery={baseQuery} columns={defaultColumns} />
    </Stack>
  )
}
